import { useState } from 'react';

export interface SavedFilterPreset<T> {
  id: string;
  name: string;
  filters: T;
}

const storageKeyFor = (pageKey: string) => `imc-saved-filters:${pageKey}`;

/** Persists named filter presets per page to localStorage — a frontend-only convenience,
 * no backend concept of a "saved filter" exists (or is needed) for this. */
export function useSavedFilters<T extends Record<string, unknown>>(pageKey: string) {
  const [presets, setPresets] = useState<SavedFilterPreset<T>[]>(() => {
    try {
      const raw = localStorage.getItem(storageKeyFor(pageKey));
      return raw ? (JSON.parse(raw) as SavedFilterPreset<T>[]) : [];
    } catch {
      return [];
    }
  });

  const persist = (next: SavedFilterPreset<T>[]) => {
    setPresets(next);
    localStorage.setItem(storageKeyFor(pageKey), JSON.stringify(next));
  };

  const savePreset = (name: string, filters: T) => {
    persist([...presets, { id: crypto.randomUUID(), name, filters }]);
  };

  const deletePreset = (id: string) => persist(presets.filter((p) => p.id !== id));

  return { presets, savePreset, deletePreset };
}
